import { Injectable, inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import type { ErsGridPreferenceStore, ErsGridPrefs } from '@rcs/grid';
import type { AllGridViews, GenericPair, SaveUserPreferencesDataQuery } from '@rcs/utils';
import { ErsGenericExtService } from './ers-generic-ext/ers-generic-ext.service';
import { selectTenantUserId } from '../base/+store/selectors/ers-user.selectors';
import { parsePreferenceData } from './user-view-preference/normalize-preference';

/**
 * API-backed grid preference store. Persists per-user, per-grid column state,
 * filter model and pagination to the backend Time Service — same endpoint the
 * legacy `UserViewPreferenceService` uses — so preferences survive across
 * browsers and devices.
 *
 * Grid IDs (produced by {@link RouteErsGridIdResolver}) are matched to backend
 * `AllGridViews` records via `gridViewCode`. If no matching record exists the
 * preference is silently skipped (the grid just uses defaults).
 *
 * Replaces the default `LocalStorageErsGridPreferenceStore` when provided
 * against the `ERS_GRID_PREFERENCE_STORE` injection token.
 */
@Injectable({ providedIn: 'root' })
export class ApiErsGridPreferenceStore implements ErsGridPreferenceStore {
  private readonly _gService = inject(ErsGenericExtService);
  private readonly _store = inject(Store);

  /** Cache grid views so we only fetch once per session. */
  private _gridViewsCache: AllGridViews[] | null = null;
  /** In-flight grid views fetch (dedup). */
  private _gridViewsPromise: Promise<AllGridViews[]> | null = null;

  async load(gridId: string): Promise<ErsGridPrefs | null> {
    try {
      const [userId, viewRef] = await Promise.all([
        this.getUserId(),
        this.resolveViewRef(gridId),
      ]);
      if (!userId || !viewRef) return null;

      const res = await firstValueFrom(
        this._gService.initiateGetRequest<{ prefrencesData?: string }>(
          'fetchUserPreferenceByUserIdandPunchinViewId',
          [{ userId, punchinViewId: viewRef.id }],
        ),
      );
      if (!res?.prefrencesData) return null;

      const parsed = parsePreferenceData(res.prefrencesData);

      // Map the API shape → ErsGridPrefs
      return {
        columnState: parsed.columnState ?? null,
        filterModel: parsed.filterModel ?? null,
        paginationPageSize: parsed.paginationPageSize ?? undefined,
        quickSearchText: parsed.quickSearchText || undefined,
      };
    } catch (err) {
      console.error('[ApiErsGridPreferenceStore] load failed', err);
      return null;
    }
  }

  save(gridId: string, prefs: ErsGridPrefs): void {
    // Fire-and-forget — errors are logged, never thrown.
    this.saveAsync(gridId, prefs).catch((err) =>
      console.error('[ApiErsGridPreferenceStore] save failed', err),
    );
  }

  private async saveAsync(gridId: string, prefs: ErsGridPrefs): Promise<void> {
    const [userId, viewRef] = await Promise.all([
      this.getUserId(),
      this.resolveViewRef(gridId),
    ]);
    if (!userId || !viewRef) return;

    const payload: SaveUserPreferencesDataQuery = {
      userId,
      viewReferenceId: viewRef.id,
      preferencesData: JSON.stringify({
        columnState: prefs.columnState ?? [],
        filterModel: prefs.filterModel ?? {},
        paginationPageSize: prefs.paginationPageSize,
        quickSearchText: prefs.quickSearchText || '',
      }),
    };

    await firstValueFrom(
      this._gService.initiatePostRequest<SaveUserPreferencesDataQuery, GenericPair>(
        'savePunchInViewUserPrefrences',
        'SaveUserPreferencesDataQuery',
        payload,
      ),
    );
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private async getUserId(): Promise<string | undefined> {
    return firstValueFrom(
      this._store.pipe(
        select(selectTenantUserId),
        filter((id): id is string => !!id),
        take(1),
      ),
    );
  }

  /** Find the backend `AllGridViews` record whose `gridViewCode` matches the gridId. */
  private async resolveViewRef(gridId: string): Promise<AllGridViews | undefined> {
    const views = await this.fetchGridViews();
    return views.find((v) => v.gridViewCode === gridId);
  }

  private async fetchGridViews(): Promise<AllGridViews[]> {
    if (this._gridViewsCache) return this._gridViewsCache;
    if (!this._gridViewsPromise) {
      this._gridViewsPromise = firstValueFrom(
        this._gService.initiateGetRequest<AllGridViews[]>('fetchAllGridView'),
      )
        .then((views) => {
          this._gridViewsCache = views ?? [];
          return this._gridViewsCache;
        })
        .catch((err) => {
          console.error('[ApiErsGridPreferenceStore] fetchGridViews failed', err);
          this._gridViewsPromise = null;
          return [];
        });
    }
    return this._gridViewsPromise;
  }
}
